import dayjs from 'dayjs';
import React, { createContext, useContext, useMemo } from 'react';

import useConstructionSiteData from 'business/user/provider/constructionSite';
import useUserData from 'business/user/provider/user';
import { getAllowedRoles } from 'business/user/services/authentication';
import { ROLES } from 'business/user/services/config';
import {
  userHasRole,
  userIsDataManager,
} from 'business/user/services/permissions';
import {
  CommonUserFieldsFragment,
  ConstructionSiteFieldsFragment,
  Module_Enum,
} from 'generated/graphql';
import logger from 'technical/logger';
import timezoneManagement from 'technical/time-utils/dayjs.timezone';

export interface AppContext {
  user: CommonUserFieldsFragment | undefined;
  /**
   * @deprecated Use usePermission() context instead
   */
  isDataManager: boolean;
  /**
   * Shift Report module scope
   *
   * @deprecated Use usePermission() context instead
   */
  isConstructionSiteManager: boolean;
  /**
   * Shift Report module scope
   *
   * @deprecated Use usePermission() context instead
   */
  isConstructionSiteFullReader: boolean;
  /**
   * Shift Report module scope
   *
   * @deprecated Use usePermission() context instead
   */
  isConstructionSiteReader: boolean;
  /**
   * Shift Report module scope
   *
   * @deprecated Use usePermission() context instead
   */
  isConstructionSiteUser: boolean;
  /**
   * Shift Report module scope
   *
   * @deprecated Use usePermission() context instead
   */
  hasManagerReadPermission: boolean;
  /**
   * Shift Report module scope
   *
   * @deprecated Use usePermission() context instead
   */
  hasManagerEditPermission: boolean;
  constructionSites: ConstructionSiteFieldsFragment[];
  currentConstructionSiteId: string | undefined;
  currentConstructionSite: ConstructionSiteFieldsFragment | null;
  setCurrentConstructionSiteId: (constructionSiteId: string) => void;
  isConnected: boolean;
  appBootstraped: boolean;
  requestRebootstrap: () => Promise<void>;
}

const AppContext = createContext<AppContext>({
  user: undefined,
  isDataManager: false,
  isConstructionSiteManager: false,
  isConstructionSiteFullReader: false,
  isConstructionSiteReader: false,
  isConstructionSiteUser: false,
  hasManagerReadPermission: false,
  hasManagerEditPermission: false,
  constructionSites: [],
  currentConstructionSiteId: undefined,
  currentConstructionSite: null,
  setCurrentConstructionSiteId: () => undefined,
  isConnected: false,
  appBootstraped: false,
  requestRebootstrap: () => Promise.resolve(),
});

interface Props {
  children: React.ReactNode;
}
export function AppProvider({ children }: Props) {
  const { isBootstraped: userIsBootstraped, ...userData } = useUserData();
  const {
    isBootstraped: constructionSiteIsBootstraped,
    ...constructionSiteData
  } = useConstructionSiteData(userData?.user);

  const userAllowedRoles = getAllowedRoles();

  const shiftReportUserRoles = useMemo(
    () => ({
      isDataManager: userIsDataManager(userAllowedRoles),
      isConstructionSiteManager: userHasRole(
        ROLES.CONSTRUCTION_SITE_MANAGER,
        constructionSiteData.currentConstructionSite,
        userData.user,
        Module_Enum.ShiftReport,
      ),
      isConstructionSiteFullReader: userHasRole(
        ROLES.CONSTRUCTION_SITE_FULL_READER,
        constructionSiteData.currentConstructionSite,
        userData.user,
        Module_Enum.ShiftReport,
      ),
      isConstructionSiteReader: userHasRole(
        ROLES.CONSTRUCTION_SITE_READER,
        constructionSiteData.currentConstructionSite,
        userData.user,
        Module_Enum.ShiftReport,
      ),
      isConstructionSiteUser: userHasRole(
        ROLES.USER,
        constructionSiteData.currentConstructionSite,
        userData.user,
        Module_Enum.ShiftReport,
      ),
    }),
    [userAllowedRoles, constructionSiteData, userData],
  );

  const userComputedPermissions = useMemo(
    () => ({
      hasManagerReadPermission:
        shiftReportUserRoles.isConstructionSiteManager ||
        shiftReportUserRoles.isConstructionSiteFullReader,
      hasManagerEditPermission: shiftReportUserRoles.isConstructionSiteManager,
    }),
    [shiftReportUserRoles],
  );

  const contextValue = useMemo(() => {
    return {
      ...userData,
      ...shiftReportUserRoles,
      ...userComputedPermissions,
      ...constructionSiteData,
      appBootstraped: userData.user
        ? userIsBootstraped && constructionSiteIsBootstraped
        : userIsBootstraped,
    };
  }, [
    userData,
    shiftReportUserRoles,
    userComputedPermissions,
    constructionSiteData,
    constructionSiteIsBootstraped,
    userIsBootstraped,
  ]);

  logger.info(
    'AppProvider',
    userData,
    constructionSiteData,
    // Get current default timezone info
    timezoneManagement.getDefaultTz(),
    dayjs.locale(),
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}

export const useAppContext = () => useContext(AppContext);
